import { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import HeadphonesIcon from '@mui/icons-material/Headphones'
import LockIcon from '@mui/icons-material/Lock'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import { useChat } from '../../context/useChat'
import { useAuth } from '../../context/useAuth'
import { useToast } from '../../context/useToast'
import { useWorkspace } from '../../context/useWorkspace'
import { useNls } from '../../nls/nlsProvider'
import { HuddleStrip } from './HuddleStrip'
import { MemberManagerPanel } from './MemberManagerPanel'
import { Button, StatusChip } from '../Common'
import { isWhatsappChannel, maskWhatsappNumber, replyWindow } from '../../utils/whatsappChat'
import styles from './chat.module.css'

export const ChannelHeader = () => {
  const { channelsList, activeChannelId, huddlePresence, setHuddleState } = useChat()
  const { currentUser } = useAuth()
  const { showToast } = useToast()
  const { workspaces = [], activeWorkspaceId } = useWorkspace()
  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId)
  const { t } = useNls()

  const [isMemberPanelOpen, setIsMemberPanelOpen] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  const channel = useMemo(
    () => channelsList.find((c) => c.id === activeChannelId) ?? null,
    [channelsList, activeChannelId],
  )

  const isWhatsapp = isWhatsappChannel(channel)

  useEffect(() => {
    if (!isWhatsapp) return
    const timer = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(timer)
  }, [isWhatsapp])

  const currentWorkspace = activeWorkspace ?? workspaces.find((w) => w.id === activeWorkspaceId)
  const route = currentWorkspace?.settings?.whatsapp?.route === 'OWN' ? 'OWN' : 'TIMELINING'

  const memberCount = channel
    ? channel.type === 'private'
      ? Object.keys(channel.roles).length
      : null
    : null

  const isInHuddle = Boolean(
    currentUser && huddlePresence.some((p) => p.uid === currentUser.uid),
  )

  if (!channel) {
    return (
      <header className={styles.header}>
        <span className={styles.headerName}>{t('NLSChatTitleSelectChannel')}</span>
        <span className={styles.headerMeta}>{t('NLSChatSubtitlePickChannel')}</span>
      </header>
    )
  }

  const handleHuddleToggle = async () => {
    try {
      await setHuddleState(
        isInHuddle ? { isListening: false, isSpeaking: false } : { isListening: true, isSpeaking: false },
      )
    } catch (error) {
      const message = error instanceof Error ? error.message : t('NLSChatErrorUpdateHuddleFailed')
      showToast(message, 'error')
    }
  }

  return (
    <motion.header
      className={styles.header}
      key={channel.id}
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.16, ease: 'easeOut' }}
    >
      <div>
        <div className={styles.headerName}>
          {channel.type === 'private' ? (
            <LockIcon style={{ fontSize: 16, verticalAlign: '-3px', marginRight: 6 }} />
          ) : (
            <span className={styles.channelHash} style={{ marginRight: 6 }}>#</span>
          )}
          {channel.slug || channel.name}
        </div>
        {isWhatsappChannel(channel) ? (
          <div className={styles.headerMeta} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
            {channel.whatsappE164 && (
              <span data-testid="whatsapp-header-mask">{maskWhatsappNumber(channel.whatsappE164)}</span>
            )}
            <StatusChip tone="neutral" size="sm" data-testid="whatsapp-header-route">
              {route === 'OWN' ? t('NLSChatRouteOwn') : t('NLSChatRouteTimelining')}
            </StatusChip>
            {(() => {
              const windowInfo = replyWindow(channel.whatsappLastInboundAt, now)
              return (
                <span data-testid="whatsapp-header-window">
                  <StatusChip tone={windowInfo.open ? 'info' : 'muted'} size="sm">
                    {windowInfo.open
                      ? t('NLSChatReplyWindowLeft', { hours: Math.max(1, Math.ceil(windowInfo.msLeft / (60 * 60 * 1000))) })
                      : t('NLSChatReplyWindowClosed')}
                  </StatusChip>
                </span>
              )
            })()}
            <span data-testid="whatsapp-header-analysis">
              <StatusChip tone="muted" size="sm">
                {t('NLSChatAnalysisOff')}
              </StatusChip>
            </span>
          </div>
        ) : (
          <div className={styles.headerMeta}>
            {channel.type === 'private' ? t('NLSChatLabelPrivateChannel') : t('NLSChatLabelPublicChannel')}
            {memberCount !== null && ` · ${t('NLSChatLabelMemberCount', { count: memberCount, plural: memberCount === 1 ? '' : 's' })}`}
          </div>
        )}
      </div>

      {channel.type === 'private' && (
        <Button
          variant="tertiary"
          size="sm"
          icon={<PeopleAltOutlinedIcon />}
          onClick={() => setIsMemberPanelOpen(true)}
          aria-label={t('NLSChatActionManageMembers')}
        >
          {t('NLSChatLabelMembers')}
        </Button>
      )}

      <HuddleStrip />

      <Button
        variant={isInHuddle ? 'secondary' : 'primary'}
        size="sm"
        icon={<HeadphonesIcon />}
        onClick={handleHuddleToggle}
        disabled={!activeChannelId}
      >
        {isInHuddle ? t('NLSChatHuddleLeave') : t('NLSChatHuddleJoin')}
      </Button>

      {isMemberPanelOpen && channel.type === 'private' && (
        <MemberManagerPanel
          channelId={channel.id}
          onClose={() => setIsMemberPanelOpen(false)}
        />
      )}
    </motion.header>
  )
}
